import { Close } from '@mui/icons-material';
import {
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import SidePanelDialog from 'src/globalComponents/SidePanelDialog';
import AccessoryDataCard from './AccessoryDataCard';
import { useApi } from 'src/hooks/useApi';
import Accessory, { AccessoryMaterial } from 'src/types/shared/accessory';
import AccessoryMaterialDataCard from './AccessoryMaterialDataCard';
import AddIcon from '@mui/icons-material/Add';
import verifyAccessory from 'src/utils/verifyAccessory';
import { toastSuccess } from 'src/Toaster/Toast';
import Spinner from 'src/globalComponents/spinner';
import SpinnerSize from 'src/enums/spinnerSize';

function getDefaultAccessory() {
	const defaultAccessory: Accessory = {
		name: '',
		calculationType: '',
		description: '',
		materials: [],
	};

	return defaultAccessory;
}

function getDefaultLabor() {
	const defaultLabor: AccessoryMaterial = {
		name: 'Labor',
		unitsPerAccessory: 1,
		maxAccessories: 1,
		price: 0,
		category: 'Labor',
	};

	return defaultLabor;
}

function getDefaultMaterial() {
	const defaultMaterial: AccessoryMaterial = {
		name: '',
		unitsPerAccessory: 1,
		maxAccessories: 1,
		price: 0,
		category: 'Material',
	};

	return defaultMaterial;
}

const AddAccessory: React.FC<{
	key: any;
	isOpen: boolean;
	onClose: () => void;
	onRefresh: () => void;
}> = ({ key, isOpen, onClose, onRefresh }) => {
	const api = useApi();
	const [calculationTypes, setCalculationTypes] = useState([]);
	const [accessoryData, setAccessoryData] =
		useState<Accessory>(getDefaultAccessory);
	const [error, setError] = useState<string | null>(null);
	const [apiError, setApiError] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		(async () => {
			setCalculationTypes(await api.accessory.getAccessoryCalculationTypes());
		})();
	}, []);

	useEffect(() => {
		setError(verifyAccessory(accessoryData));
		setApiError(null);
	}, [accessoryData]);

	const accessoryMaterials = accessoryData.materials;

	accessoryMaterials.sort((mat1, mat2) =>
		mat2.category.localeCompare(mat1.category)
	);

	const addAccessory = async () => {
		if (error) {
			return;
		}

		setIsSaving(true);

		const data = {
			...accessoryData,
			materials: accessoryData.materials.map((material) => ({
				...material,
				...(material.category == 'Labor'
					? { name: `${accessoryData.name} Labor` }
					: {}),
			})),
		};

		const result = await api.accessory.createAccessory(data);

		setIsSaving(false);

		if (result.errored) {
			setApiError(result.error.message);
		} else {
			toastSuccess({ description: 'Accessory created successfully' });
			onRefresh();
			onClose();
		}
	};

	return (
		<SidePanelDialog
			key={key}
			open={isOpen}
			onClose={onClose}
			keepMounted={false}
			closeAfterTransition={true}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: '0px',
				}}
			>
				<Stack>
					<Typography fontWeight="bold">Accessories</Typography>
					<Typography fontSize="13px" color="gray.500">
						Configure accessories and their pricing formulas
					</Typography>
				</Stack>

				<IconButton onClick={onClose}>
					<Close />
				</IconButton>
			</DialogTitle>

			<Divider sx={{ marginTop: '10px' }} />

			{apiError && (
				<Typography
					sx={{
						color: 'error.main',
						fontSize: '13px',
						marginTop: '10px',
					}}
				>
					{apiError?.charAt(0).toUpperCase() + apiError?.slice(1)}
				</Typography>
			)}

			<DialogContent
				sx={{
					padding: '0px',
				}}
			>
				<AccessoryDataCard
					accessoryData={accessoryData}
					setAccessoryData={setAccessoryData}
					calculationTypes={calculationTypes}
				/>

				<Box
					sx={{
						marginTop: '20px',
						borderRadius: '10px',
						padding: '10px',
						border: '1px solid #E5E7EB',
						paddingBottom: '20px',
					}}
				>
					<Stack>
						<Typography fontWeight="bold">Include Materials & Labor</Typography>

						<Stack
							display="grid"
							gridTemplateColumns="1fr 1fr"
							gap="10px"
							sx={{ marginTop: '10px' }}
						>
							<Button
								color="primary"
								startIcon={<AddIcon />}
								sx={{}}
								onClick={() =>
									setAccessoryData((prev) => ({
										...prev,
										materials: [...prev.materials, getDefaultMaterial()],
									}))
								}
							>
								Add Material
							</Button>

							<Button
								color="primary"
								startIcon={<AddIcon />}
								onClick={() =>
									setAccessoryData((prev) => ({
										...prev,
										materials: [...prev.materials, getDefaultLabor()],
									}))
								}
							>
								Add Labor
							</Button>
						</Stack>
					</Stack>

					{accessoryData.materials.map((accessoryMaterial, index) => (
						<AccessoryMaterialDataCard
							key={`${key}-${index}`}
							index={index}
							accessoryMaterialData={accessoryMaterial}
							setAccessoryData={setAccessoryData}
							accessoryData={accessoryData}
						/>
					))}
				</Box>
			</DialogContent>

			<DialogActions>
				<Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '25px' }}>
					<Button sx={{ color: 'black' }} onClick={onClose}>
						Cancel
					</Button>

					<Button
						sx={{
							backgroundColor: 'primary.main',
							color: 'white',
						}}
						onClick={addAccessory}
						disabled={Boolean(error) || isSaving}
					>
						{isSaving ? <Spinner size={SpinnerSize.SMALL} /> : 'Add Accessory'}
					</Button>
				</Stack>
			</DialogActions>
		</SidePanelDialog>
	);
};

export default AddAccessory;
